/* Homepage click tracking.
   Publishes Shopify.analytics custom events for the homepage's interactive elements
   so a custom pixel can subscribe to them and forward the clicks to Google Analytics.
   See https://help.shopify.com/en/manual/promoting-marketing/pixels/custom-pixels/code

   The custom pixel subscribes with analytics.subscribe("<event>", handler); the values
   published here are available on the event's customData.

   Every payload includes "section" (the section id from the homepage template) so the
   dashboard can tell which section the click came from. Events:
     homepage_banner_cta_click           { label, url }
     homepage_product_card_cta_click     { product, label, url }
     homepage_product_card_swatch_click  { product, colorway }
     homepage_video_toggle_click         { action }            "pause" | "play"
     homepage_showcase_item_click        { item }
     homepage_showcase_control_click     { control, item, index, action }
                                         control: "previous" | "next" | "dot" | "pause"
     homepage_app_tab_click              { tab }
     homepage_app_dot_click              { index }
     homepage_app_store_badge_click      { store, url }        "apple" | "google"
     homepage_colorway_click             { colorway }
     homepage_blog_post_click            { title, link, url }  link: "image" | "title" | "read_more"
*/
(function () {
  'use strict';

  // Don't track clicks made while editing in the theme editor.
  if (window.Shopify && window.Shopify.designMode) return;

  function publish(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (error) {
      /* Tracking must never break the page. */
    }
  }

  function sectionOf(element) {
    var wrapper = element.closest('.shopify-section');
    if (!wrapper || typeof wrapper.id !== 'string' || wrapper.id.indexOf('shopify-section-') !== 0) return '';
    return wrapper.id.replace('shopify-section-', '');
  }

  function clean(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  function textOf(element, selector) {
    var found = element.querySelector(selector);
    return found ? clean(found.textContent) : '';
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    // Banner buttons (hero, smoke, outdoor air quality, 30-day trial banners).
    var bannerButton = target.closest('.custom__banner a.btn-1');
    if (bannerButton) {
      publish('homepage_banner_cta_click', {
        section: sectionOf(bannerButton),
        label: clean(bannerButton.textContent),
        url: bannerButton.href || ''
      });
      return;
    }

    // Product cards: colorway swatches and the card's Shop now link.
    var productCard = target.closest('[data-pc-card]');
    if (productCard) {
      var product = textOf(productCard, '.custom__pc-card-title');
      var productSwatch = target.closest('[data-pc-swatch]');
      if (productSwatch) {
        publish('homepage_product_card_swatch_click', {
          section: sectionOf(productSwatch),
          product: product,
          colorway: productSwatch.getAttribute('data-pc-name') || ''
        });
        return;
      }
      var cardLink = target.closest('[data-pc-link]');
      if (cardLink) {
        publish('homepage_product_card_cta_click', {
          section: sectionOf(cardLink),
          product: product,
          label: clean(cardLink.textContent),
          url: cardLink.href || ''
        });
        return;
      }
    }

    // Video with text: pause/play toggle.
    var videoToggle = target.closest('[data-video-toggle]');
    if (videoToggle) {
      var videoRoot = videoToggle.closest('[data-video-full]');
      publish('homepage_video_toggle_click', {
        section: sectionOf(videoToggle),
        action: videoRoot && videoRoot.classList.contains('is-playing') ? 'pause' : 'play'
      });
      return;
    }

    // Interactive showcase (pollutants): item triggers, arrows, dots, pause.
    var showcaseRoot = target.closest('[data-pshow]');
    if (showcaseRoot) {
      var showcaseTrigger = target.closest('[data-pshow-trigger]');
      if (showcaseTrigger) {
        var showcaseItem = showcaseTrigger.closest('[data-pshow-item]');
        publish('homepage_showcase_item_click', {
          section: sectionOf(showcaseTrigger),
          item: showcaseItem ? showcaseItem.getAttribute('data-pshow-name') || '' : ''
        });
        return;
      }
      var prevArrow = target.closest('[data-pshow-prev]');
      var nextArrow = target.closest('[data-pshow-next]');
      var showcaseDot = target.closest('[data-pshow-dot]');
      var showcasePause = target.closest('[data-pshow-pause]');
      if (prevArrow || nextArrow || showcaseDot || showcasePause) {
        var controlPayload = { section: sectionOf(showcaseRoot), item: '', index: '', action: '' };
        if (prevArrow) {
          controlPayload.control = 'previous';
        } else if (nextArrow) {
          controlPayload.control = 'next';
        } else if (showcaseDot) {
          controlPayload.control = 'dot';
          var showcaseDots = showcaseRoot.querySelectorAll('[data-pshow-dot]');
          for (var i = 0; i < showcaseDots.length; i++) {
            if (showcaseDots[i] === showcaseDot) {
              controlPayload.index = i;
              break;
            }
          }
          controlPayload.item = showcaseDot.getAttribute('aria-label') || '';
        } else {
          controlPayload.control = 'pause';
          controlPayload.action = showcaseRoot.classList.contains('is-paused') ? 'play' : 'pause';
        }
        publish('homepage_showcase_control_click', controlPayload);
        return;
      }
    }

    // App slider (tabs layout): store badges, tabs, dots.
    var appRoot = target.closest('[data-app-tabs]');
    if (appRoot) {
      var storeBadge = target.closest('.app__tabs--badge');
      if (storeBadge) {
        publish('homepage_app_store_badge_click', {
          section: sectionOf(storeBadge),
          store: /google/i.test(storeBadge.getAttribute('aria-label') || '') ? 'google' : 'apple',
          url: storeBadge.href || ''
        });
        return;
      }
      var appTab = target.closest('[data-app-tab]');
      if (appTab) {
        publish('homepage_app_tab_click', {
          section: sectionOf(appTab),
          tab: clean(appTab.textContent)
        });
        return;
      }
      var appDot = target.closest('[data-app-dot]');
      if (appDot) {
        var appDots = appRoot.querySelectorAll('[data-app-dot]');
        var appIndex = '';
        for (var j = 0; j < appDots.length; j++) {
          if (appDots[j] === appDot) {
            appIndex = j;
            break;
          }
        }
        publish('homepage_app_dot_click', {
          section: sectionOf(appDot),
          index: appIndex
        });
        return;
      }
    }

    // Colorways: swatch buttons.
    var colorwaysRoot = target.closest('[data-colorways]');
    if (colorwaysRoot) {
      var colorwaySwatch = target.closest('[data-colorways-swatch]');
      if (colorwaySwatch) {
        publish('homepage_colorway_click', {
          section: sectionOf(colorwaySwatch),
          colorway: colorwaySwatch.getAttribute('aria-label') || ''
        });
        return;
      }
    }

    // Blog posts: article image, title, and "Read more" links.
    var blogRoot = target.closest('.custom__blog-v2');
    if (blogRoot) {
      var blogLink = target.closest('a');
      if (blogLink && blogRoot.contains(blogLink)) {
        var blogCard = blogLink.closest('.custom__blog-v2-card');
        var blogLinkType = '';
        if (blogLink.classList.contains('custom__blog-v2-media')) {
          blogLinkType = 'image';
        } else if (blogLink.closest('.custom__blog-v2-title')) {
          blogLinkType = 'title';
        } else if (blogLink.classList.contains('custom__blog-v2-more')) {
          blogLinkType = 'read_more';
        }
        if (blogLinkType) {
          publish('homepage_blog_post_click', {
            section: sectionOf(blogLink),
            title: blogCard ? textOf(blogCard, '.custom__blog-v2-title') : '',
            link: blogLinkType,
            url: blogLink.href || ''
          });
          return;
        }
      }
    }
  });
})();
